import {
  Button,
  Checkbox,
  CheckboxGroup,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Spacer,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import {
  CloseCircleOutline,
  DropdownInput,
  IntegrationProtocol,
  IntegrationProvider,
  IntegrationType,
  useToast,
} from '@aslanpay/portal-common-lib';

import useFetchIntegrationConfigSpec from 'app/hooks/useFetchIntegrationConfigSpec';
import useRegisterIntegration from 'app/hooks/useRegisterIntegration';

import {
  AuthConfigFields,
  ConnectionSettingsFields,
  INTEGRATION_TYPE_OPTIONS,
  PROTOCOL_OPTIONS,
  PROVIDER_OPTIONS,
  RegisterFormValues,
  buildAuthConfig,
  buildConfig,
  findMissingRequiredConfigFields,
  registerSchema,
} from '../IntegrationForm';

import { RegisterIntegrationModalProps } from './types';

function RegisterIntegrationModal({ companyId, isOpen, onClose }: RegisterIntegrationModalProps) {
  const toast = useToast();
  const { mutate: register, isLoading } = useRegisterIntegration();

  const methods = useForm<RegisterFormValues>({
    mode: 'onChange',
    resolver: yupResolver(registerSchema),
    defaultValues: {
      authType: 'NONE',
      protocol: 'REST',
      integrationTypes: [],
      configValues: {},
      configEntries: [],
    },
  });

  const {
    control,
    handleSubmit,
    reset,
    setError,
    watch,
    formState: { errors, isValid },
  } = methods;

  const provider = watch('provider');
  const protocol = watch('protocol');
  const integrationTypes = watch('integrationTypes');
  const { data: specFields = [] } = useFetchIntegrationConfigSpec(provider);

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = (data: RegisterFormValues) => {
    const missingFields = findMissingRequiredConfigFields(
      specFields,
      { protocol: data.protocol, integrationTypes: data.integrationTypes },
      data.configValues
    );
    if (missingFields.length > 0) {
      missingFields.forEach((field) =>
        setError(`configValues.${field.key}`, { message: `${field.key} is required` })
      );
      return;
    }

    register(
      {
        companyId,
        provider: data.provider as IntegrationProvider,
        protocol: data.protocol as IntegrationProtocol,
        integrationTypes: data.integrationTypes as IntegrationType[],
        baseUrl: data.baseUrl || undefined,
        config: buildConfig(data.configEntries, data.configValues),
        authConfig: buildAuthConfig(data),
      },
      {
        onSuccess: () => {
          toast({
            position: 'top-right',
            status: 'success',
            title: 'Integration registered',
            description: 'The partner integration has been successfully registered.',
          });
          handleClose();
        },
        onError: () => {
          toast({
            position: 'top-right',
            status: 'error',
            title: 'Something went wrong',
            description: 'Sorry, the integration could not be registered. Please try again.',
          });
        },
      }
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered closeOnOverlayClick={false}>
      <ModalOverlay />
      {/* The shared theme sets the dialog width to auto (content-hugging), so the two-column width
          must be explicit or the modal only grows once rows are added. */}
      <ModalContent w={{ base: '100vw', sm: '90vw', md: '760px' }} maxH="85vh" overflowY="auto">
        <ModalHeader>
          <HStack>
            <Text as="h6">Register partner integration</Text>
            <Spacer />
            <IconButton
              variant="link"
              icon={<CloseCircleOutline boxSize="30px" />}
              aria-label="Close register integration"
              onClick={handleClose}
            />
          </HStack>
        </ModalHeader>
        <ModalBody>
          <FormProvider {...methods}>
            <form id="register-integration-form" onSubmit={handleSubmit(onSubmit)} noValidate>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing="24px" alignItems="flex-start">
                <VStack spacing="16px" align="stretch">
                  <FormControl isInvalid={!!errors.provider} isRequired>
                    <FormLabel>Provider</FormLabel>
                    <Controller
                      control={control}
                      name="provider"
                      render={({ field: { onChange, onBlur, value } }) => (
                        <DropdownInput
                          key={value ?? 'none'}
                          placeholder="Select a provider"
                          options={PROVIDER_OPTIONS}
                          defaultValue={PROVIDER_OPTIONS.find((o) => o.value === value)}
                          onChange={onChange}
                          onBlur={onBlur}
                        />
                      )}
                    />
                    <FormErrorMessage>{errors.provider?.message}</FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={!!errors.protocol} isRequired>
                    <FormLabel>Protocol</FormLabel>
                    <Controller
                      control={control}
                      name="protocol"
                      render={({ field: { onChange, onBlur, value } }) => (
                        <DropdownInput
                          key={value ?? 'REST'}
                          placeholder="Select a protocol"
                          options={PROTOCOL_OPTIONS}
                          defaultValue={PROTOCOL_OPTIONS.find((o) => o.value === value)}
                          onChange={onChange}
                          onBlur={onBlur}
                        />
                      )}
                    />
                    <FormErrorMessage>{errors.protocol?.message}</FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={!!errors.integrationTypes} isRequired>
                    <FormLabel>Integration types</FormLabel>
                    <Controller
                      control={control}
                      name="integrationTypes"
                      render={({ field: { onChange, value } }) => (
                        <CheckboxGroup value={value} onChange={onChange}>
                          <VStack align="flex-start" spacing="6px">
                            {INTEGRATION_TYPE_OPTIONS.map((option) => (
                              <Checkbox key={option.value} value={option.value}>
                                {option.text}
                              </Checkbox>
                            ))}
                          </VStack>
                        </CheckboxGroup>
                      )}
                    />
                    <FormErrorMessage>{errors.integrationTypes?.message}</FormErrorMessage>
                  </FormControl>

                  <ConnectionSettingsFields
                    provider={provider}
                    protocol={protocol}
                    integrationTypes={integrationTypes}
                  />
                </VStack>

                <VStack spacing="16px" align="stretch">
                  <AuthConfigFields />
                </VStack>
              </SimpleGrid>
            </form>
          </FormProvider>
        </ModalBody>
        <ModalFooter>
          <HStack>
            <Button variant="outline" size="sm" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              form="register-integration-form"
              size="sm"
              isDisabled={!isValid}
              isLoading={isLoading}
            >
              Register integration
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default RegisterIntegrationModal;
